import React, { useEffect, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { Box, Button } from "@mui/material";
import { useGetBlobsQuery } from "api/useGetBlobsQuery";
import { Alerts } from "components/shared/Alerts/Alerts";
import { usePollingSearchStatus } from "hooks/usePollingSerchStatus";
import { AlertColor, TEAMS_SOURCE } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";

import TeamsLogo from "../../assets/img/TeamsLogo.png";
import "../../css/documentManagement.css";

import DocumentsGrid from "./DocumentsGrid";
import { TeamsIntegrationModal } from "@/components/DocumentManagementView/modals/TeamsIntegrationModal";
import BackgroundContainer from "@/components/shared/BackgroundTasks/BackgroundContainer";
import { DeleteModal } from "@/components/shared/Modal/DeleteModal";
import { EditModal } from "@/components/shared/Modal/EditModal";
import { MoveModal } from "@/components/shared/Modal/MoveModal";
import { OverrideModal } from "@/components/shared/Modal/OverrideModal";
import { UploadFileModal } from "@/components/shared/Modal/UploadFile";
import { useBackground } from "@/context/BackgroundContext";
import { usePermissions } from "@/context/PermissionsContext";
import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";
import { BackgroundTask } from "@/util/interfaces/background/BackgroundTask";
import { Permission } from "@/utils/policies";

export const DocumentManagementView: React.FC<{ teamsIntegrationFlag?: boolean }> = ({
  teamsIntegrationFlag = true,
}) => {
  const { getBackgroundTasks, removeBackgroundTask } = useBackground();

  const tasks: BackgroundTask[] = [...getBackgroundTasks()];
  const { hasPermission } = usePermissions();
  const [showUploadFilesModal, setShowUploadFilesModal] = useState<
    "upload_file" | "create_folder" | null
  >(null);
  const [filesToDelete, setFilesToDelete] = useState<DocumentData[]>([]);
  const [filesToMove, setFilesToMove] = useState<DocumentData[]>([]);
  const [fileToEdit, setFileToEdit] = useState<DocumentData | null>(null);
  const [fileToOverride, setFileToOverride] = useState<DocumentData | null>(null);
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [selectedPath, setSelectedPath] = useState<{ id: string; name: string }[]>([]);
  const [selectedRows, setSelectedRows] = useState<DocumentData[]>([]);
  const [openTeamsIntegrationModal, setOpenTeamsIntegrationModal] = useState<boolean>(false);
  const [lastRefetchTime, setLastRefetchTime] = useState<Date>(new Date(0));
  const { data: documents, isError, refetch, isLoading } = useGetBlobsQuery();
  const { msalConfig } = useTeamsAndChannels();
  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();
  const isLoggedIn = accounts.length > 0;
  usePollingSearchStatus({ documents });
  useEffect(() => {
    if (!isError) return;

    setAlertColor(AlertColor.Error);
    setAlertMessage("Unexpected error while calling the reactor.");
  }, [isError]);
  const getTokenAndOpenModal = async () => {
    try {
      await instance.loginPopup(msalConfig.loginRequest);
      setOpenTeamsIntegrationModal(true);
    } catch (loginError) {
      console.error("Login error:", loginError);
      setAlertColor(AlertColor.Error);
      setAlertMessage("Authentication failed. Please try again.");
    }
  };
  const handleLogin = async () => {
    if (isLoggedIn) {
      try {
        const account = accounts[0];
        if (!msalConfig.loginRequest) {
          throw new Error("MSAL loginRequest configuration is missing.");
        }
        await instance.acquireTokenSilent({
          ...msalConfig.loginRequest,
          scopes: msalConfig.loginRequest.scopes ?? ["User.Read"],
          account: account,
        });
        // Si llegamos aquí, el token es válido
        setOpenTeamsIntegrationModal(true);
      } catch (error) {
        console.warn("Token expired or invalid, re-authenticating...", error);
        await getTokenAndOpenModal();
      }
    } else {
      await getTokenAndOpenModal();
    }
  };
  const editFileLocalStorage = (fileEdited: DocumentData) => {
    const localStorageValue = localStorage.getItem(`doc-${fileEdited.id}`);
    if (localStorageValue) {
      localStorage.setItem(`doc-${fileEdited.id}`, JSON.stringify(fileEdited));
    }
  };
  return (
    <>
      <Box
        className="flex flow-column gap-5"
        data-testid="doc_manag"
        alignItems="center"
        sx={{
          background: "#F8F8F8",
        }}
      >
        <Box sx={{ background: "white", width: "100%", padding: "24px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span className="title">Document Management</span>
            {teamsIntegrationFlag && hasPermission(Permission.IMPORT_TEAMS) && (
              <Button
                data-testid="teamsSyncButton"
                variant="outlined"
                color="purple"
                onClick={handleLogin}
              >
                <Box
                  component="img"
                  src={TeamsLogo}
                  alt="teams icon"
                  width="20px"
                  sx={{ marginRight: "5px" }}
                />
                Import Teams
              </Button>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            margin: "24px",
            width: "calc(100% - 48px)",
            background: "white",
            borderRadius: "8px",
          }}
        >
          <DocumentsGrid
            isDocumentsLoading={isLoading}
            blobs={documents}
            callbackAddFiles={(modalName) => setShowUploadFilesModal(modalName)}
            callbackDeleteFile={(files: DocumentData[]) => setFilesToDelete(files)}
            callbackMoveFiles={(files: DocumentData[]) => setFilesToMove(files)}
            callbackEditFile={(file: DocumentData) => setFileToEdit(file)}
            callbackOverrideFile={(file: DocumentData) => setFileToOverride(file)}
            setSelectedPath={setSelectedPath}
            selectedPath={selectedPath}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </Box>
        {tasks.length > 0 && (
          <BackgroundContainer
            tasks={tasks}
            onDismissTask={(taskId: string) => {
              removeBackgroundTask(taskId);
              refetch();
            }}
            callbackRefetch={() => {
              const now = new Date();
              const timeDiff = now.getTime() - lastRefetchTime.getTime();
              if (timeDiff >= 10000) {
                // 10 seconds on milisegundos
                setLastRefetchTime(now);
                refetch();
              }
            }}
          />
        )}
      </Box>
      <Alerts
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
      {showUploadFilesModal && documents && (
        <UploadFileModal
          showModal={showUploadFilesModal}
          selectedPath={selectedPath}
          documents={
            showUploadFilesModal === "create_folder"
              ? documents
              : documents.filter((d) => {
                  const path =
                    selectedPath.length > 0 ? selectedPath.map((p) => p.name).join("/") + "/" : "";
                  return d.path === path;
                })
          }
          callbackClose={(updateFiles, _numFiles) => {
            setShowUploadFilesModal(null);
            if (updateFiles) {
              setSelectedRows([]);
              refetch();
            }
          }}
        />
      )}
      {filesToDelete.length > 0 && (
        <DeleteModal
          documents={filesToDelete}
          selectedRows={selectedRows.length > 0 ? selectedRows : filesToDelete}
          callbackClose={(update) => {
            setFilesToDelete([]);
            if (update) {
              setAlertMessage("You have successfully deleted selected file(s).");
              setAlertColor(AlertColor.Success);
              setSelectedRows([]);
              refetch();
            }
          }}
        />
      )}
      {filesToMove.length > 0 && (
        <MoveModal
          currentPath={selectedPath}
          selectedDocuments={filesToMove}
          availableFolders={documents}
          callbackClose={(update) => {
            setFilesToMove([]);
            if (update) {
              setAlertMessage("You have successfully move selected item(s).");
              setAlertColor(AlertColor.Success);
              setSelectedRows([]);
              refetch();
            }
          }}
        />
      )}
      {fileToEdit && (
        <EditModal
          documents={documents}
          selectedPath={selectedPath}
          callbackClose={(fileEdited: DocumentData | null) => {
            setFileToEdit(null);
            if (fileEdited) {
              setAlertMessage("You have successfully edited selected file(s).");
              setAlertColor(AlertColor.Success);
              refetch();
              editFileLocalStorage(fileEdited);
            }
          }}
          file={fileToEdit}
        />
      )}
      {fileToOverride && (
        <OverrideModal
          callbackClose={(fileEdited: DocumentData | null) => {
            setFileToOverride(null);
            if (fileEdited) {
              setAlertMessage("You have successfully overrided selected file.");
              setAlertColor(AlertColor.Success);
              refetch();
              editFileLocalStorage(fileEdited);
            }
          }}
          file={fileToOverride}
        />
      )}
      {openTeamsIntegrationModal && (
        <TeamsIntegrationModal
          documents={documents?.filter((d) => d.folderSource === TEAMS_SOURCE) || []}
          callbackClose={(_message, _color) => {
            if (_message && _color) {
              setAlertColor(_color);
              if (_color === AlertColor.Success) {
                setOpenTeamsIntegrationModal(false);
                refetch();
              } else {
                setAlertMessage(_message);
              }
            } else {
              setOpenTeamsIntegrationModal(false);
            }
          }}
        />
      )}
    </>
  );
};
